import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, Flag, RotateCcw } from 'lucide-react';
import clsx from 'clsx';

/** The Venues `flagged` URL param: only the flagged venues, only the others, or no flag filter. */
export type FlagFilterValue = 'true' | 'false' | undefined;

const FILTER_OPTIONS: { value: FlagFilterValue; label: string }[] = [
  { value: undefined, label: 'Show all' },
  { value: 'true', label: 'Show flagged' },
  { value: 'false', label: 'Show not flagged' },
];

const DROPDOWN_WIDTH = 180;

interface VenueFlagMenuProps {
  value: FlagFilterValue;
  onChange: (value: FlagFilterValue) => void;
  /** Unflag every venue, in the current filter or not. */
  onReset: () => void;
}

/** The Venues toolbar's flag button: filter the table by flag, or clear every flag. See docs/VENUE_FLAGS.md. */
export function VenueFlagMenu({ value, onChange, onReset }: VenueFlagMenuProps) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const isOpen = position !== null;

  const open = () => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const left = rect.left + DROPDOWN_WIDTH > window.innerWidth - 8
      ? Math.max(8, rect.right - DROPDOWN_WIDTH)
      : rect.left;
    setPosition({ top: rect.bottom + 4, left });
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (!buttonRef.current?.contains(target) && !dropdownRef.current?.contains(target)) {
        setPosition(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  return (
    <>
      <button
        ref={buttonRef}
        onClick={() => (isOpen ? setPosition(null) : open())}
        title="Flag filter"
        className={clsx(
          'p-2 rounded-lg border transition-colors',
          isOpen || value
            ? 'bg-primary-900/30 text-primary-400 border-primary-700/50'
            : 'bg-dark-elevated text-dark-text-muted border-dark-border hover:text-dark-text-secondary hover:bg-dark-border'
        )}
      >
        <Flag className="w-4 h-4" />
      </button>
      {isOpen && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-9999 bg-dark-elevated border border-dark-border rounded-lg shadow-xl min-w-[180px] flex flex-col overflow-hidden animate-scaleIn"
          style={{ top: position.top, left: position.left }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="py-1">
            {FILTER_OPTIONS.map((option) => {
              const isSelected = option.value === value;
              return (
                <button
                  key={option.label}
                  onClick={() => {
                    onChange(option.value);
                    setPosition(null);
                  }}
                  className={clsx(
                    'w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left transition-colors',
                    isSelected ? 'bg-primary-600/20 text-primary-300' : 'text-dark-text-secondary hover:bg-dark-surface'
                  )}
                >
                  <Check className={clsx('w-3.5 h-3.5 flex-shrink-0', !isSelected && 'invisible')} />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>
          <div className="py-1 border-t border-dark-border">
            <button
              onClick={() => {
                setPosition(null);
                onReset();
              }}
              className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-left text-red-400 hover:bg-dark-surface transition-colors"
            >
              <RotateCcw className="w-3.5 h-3.5 flex-shrink-0" />
              <span>Reset flags</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
